import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { adminFeedbackList, adminFeedbackSummary, adminFeedbackTriage, downloadFeedbackCsv, type FeedbackItem, type FeedbackStatus, type FeedbackSummary } from '../../../api/feedbackApi';
import { S, fmtWhen } from './adminPageStyles';

/** Feedback tracking (SRS #106): NPS, feature ratings, what the assistant got wrong, bugs and ideas — triaged. */

const STATUS_COLOR: Record<FeedbackStatus, string> = { new: 'var(--ds-info)', reviewed: 'var(--ds-text-secondary)', planned: 'var(--ds-warning)', done: 'var(--ds-success)', dismissed: 'var(--ds-text-faint)' };

const AdminFeedbackPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [days, setDays] = useState(90);
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [filter, setFilter] = useState({ kind: '', status: 'new' });
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});

  const load = useCallback(() => {
    adminFeedbackSummary(days).then(setSummary).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
    adminFeedbackList({ kind: filter.kind || undefined, status: filter.status || undefined, limit: 200 }).then((r) => setItems(r.items)).catch(() => {});
  }, [days, filter]);
  useEffect(() => { load(); }, [load]);

  const triage = async (id: number, status?: FeedbackStatus) => {
    try { await adminFeedbackTriage(id, { status, admin_notes: notes[id] }); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
  };

  const tile = (l: string, v: React.ReactNode, hint?: string) => (
    <div key={l} style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ds-text)' }}>{v}</div>
      <div style={{ fontSize: 12, color: 'var(--ds-text-muted)' }}>{l}</div>
      {hint && <div style={{ fontSize: 11, color: 'var(--ds-text-faint)' }}>{hint}</div>}
    </div>
  );

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Feedback" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Feedback</h2>
        <p style={S.sub}>NPS, feature ratings, why assistant replies were marked unhelpful, bug reports and ideas — from the Feedback tab of the Support button and the thumbs on chat replies. Triage each item so nothing is only counted.</p>
        {error && <div style={S.error}>{error}</div>}
        <div style={S.toolbar}>
          <label style={{ fontSize: 12.5 }}>Window <select style={S.input} value={days} onChange={(e) => setDays(Number(e.target.value))}>{[30, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}</select></label>
          <button style={S.btn} onClick={() => downloadFeedbackCsv().catch((e) => setError(e.message))}>CSV</button>
        </div>
        {summary && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
              {tile('NPS', summary.nps.score === null ? '—' : summary.nps.score, `${summary.nps.responses} responses · ${summary.nps.promoters} promoters / ${summary.nps.detractors} detractors`)}
              {tile('Chat helpful', summary.chat.helpful_pct === null ? '—' : `${summary.chat.helpful_pct}%`, `${summary.chat.up} 👍 · ${summary.chat.down} 👎`)}
              {tile('Feature ratings', summary.by_kind.feature, summary.features.length ? `lowest: ${summary.features[0].feature} ${summary.features[0].avg_rating}/5` : undefined)}
              {tile('Bugs', summary.by_kind.bug, `${summary.by_status.new} new overall`)}
              {tile('Ideas', summary.by_kind.idea, `${summary.by_status.planned} planned · ${summary.by_status.done} done`)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 14, marginBottom: 16 }}>
              <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13, background: 'var(--ds-surface-2)', borderBottom: '1px solid var(--ds-border)' }}>Feature ratings (lowest first)</div>
                <table style={S.table}><tbody>
                  {summary.features.length === 0 && <tr><td style={S.td}><span style={S.muted}>No ratings yet.</span></td></tr>}
                  {summary.features.map((f) => <tr key={f.feature}><td style={S.td}>{f.feature}</td><td style={{ ...S.td, ...S.mono }}>{f.avg_rating.toFixed(1)} / 5</td><td style={{ ...S.td, ...S.muted }}>{f.responses} responses</td></tr>)}
                </tbody></table>
              </div>
              <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13, background: 'var(--ds-surface-2)', borderBottom: '1px solid var(--ds-border)' }}>Replies marked unhelpful</div>
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {summary.unhelpful_replies.length === 0 && <div style={{ ...S.td, ...S.muted }}>None in the window.</div>}
                  {summary.unhelpful_replies.map((u) => (
                    <div key={u.message_id} style={{ padding: '8px 10px', borderBottom: '1px solid var(--ds-surface-2)', fontSize: '0.82rem' }}>
                      <div><strong>Q:</strong> {u.question || <span style={S.muted}>—</span>}</div>
                      <div style={S.muted}><strong>A:</strong> {u.reply}</div>
                      {u.why && <div style={{ color: 'var(--ds-danger-text)' }}><strong>Why:</strong> {u.why}</div>}
                      <div style={{ ...S.muted, fontSize: '0.72rem' }}>{fmtWhen(u.at)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        <div style={S.toolbar}>
          <select style={S.input} value={filter.kind} onChange={(e) => setFilter({ ...filter, kind: e.target.value })}>
            <option value="">All kinds</option>{['feature', 'nps', 'chat', 'bug', 'idea'].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select style={S.input} value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
            <option value="">All statuses</option>{(['new', 'reviewed', 'planned', 'done', 'dismissed'] as FeedbackStatus[]).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <table style={S.table}>
          <thead><tr><th style={S.th}>When</th><th style={S.th}>Kind</th><th style={S.th}>Rating</th><th style={S.th}>From</th><th style={S.th}>Comment</th><th style={S.th}>Status</th><th style={S.th}>Notes / triage</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td style={S.td} colSpan={7}><span style={S.muted}>Nothing matches.</span></td></tr>}
            {items.map((it) => (
              <tr key={it.id}>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtWhen(it.created_at)}</td>
                <td style={S.td}>{it.kind}{it.feature && <div style={{ ...S.muted, fontSize: '0.75rem' }}>{it.feature}</div>}{it.page && <div style={{ ...S.muted, fontSize: '0.72rem' }}>{it.page}</div>}</td>
                <td style={{ ...S.td, ...S.mono }}>{it.rating ?? (it.meta?.vote ? String(it.meta.vote) : '—')}</td>
                <td style={S.td}>{it.user_email || '—'}</td>
                <td style={{ ...S.td, maxWidth: 360, whiteSpace: 'pre-wrap' }}>{it.comment || <span style={S.muted}>—</span>}</td>
                <td style={S.td}><span style={{ ...S.chip, color: STATUS_COLOR[it.status] }}>{it.status}</span>{it.reviewed_by && <div style={{ ...S.muted, fontSize: '0.72rem' }}>{it.reviewed_by}</div>}</td>
                <td style={{ ...S.td, minWidth: 220 }}>
                  <input style={{ ...S.input, width: '100%', minWidth: 0 }} placeholder={it.admin_notes || 'notes'} value={notes[it.id] ?? ''} onChange={(e) => setNotes({ ...notes, [it.id]: e.target.value })} />
                  <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                    {(['reviewed', 'planned', 'done', 'dismissed'] as FeedbackStatus[]).map((st) => <button key={st} style={{ ...S.btn, padding: '2px 8px', fontSize: '0.75rem' }} onClick={() => triage(it.id, st)}>{st}</button>)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </DashboardLayout>
  );
};

export default AdminFeedbackPage;
